import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Rating,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link as MuiLink,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

type ReviewStatus = 'published' | 'hidden' | 'cancelled';

const STATUS_LABEL: Record<ReviewStatus, string> = {
  published: '已發布',
  hidden: '已隱藏',
  cancelled: '已取消',
};

interface ReviewCustomer {
  id: number;
  name: string;
  email: string;
  nickname: string | null;
}

interface AdminReview {
  id: string;
  productId: string;
  brand: 'metanoia' | 'mdoc';
  rating: number;
  title: string;
  body: string;
  imageUrls: string[];
  status: ReviewStatus;
  createdAt: string;
  updatedAt: string;
  author: { displayName: string };
  customer: ReviewCustomer;
  productTitle: string | null;
  adminNote: string | null;
}

interface CreditAction {
  reversed?: number;
  granted?: number;
}

interface StatusResponse {
  success: boolean;
  status: ReviewStatus;
  creditAction: CreditAction | null;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const PAGE_LIMIT = 50;

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(
      d.getDate()
    ).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(
      2,
      '0'
    )}`;
  } catch {
    return iso;
  }
}

function storefrontUrl(brand: 'metanoia' | 'mdoc', productId: string): string {
  const host = brand === 'mdoc' ? 'https://mdoc.tw' : 'https://metanoia.tw';
  return `${host}/shop/${productId}`;
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const ReviewsAdminPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [statusFilter, setStatusFilter] = useState<ReviewStatus>('published');
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Moderate dialog state
  const [dialogReview, setDialogReview] = useState<AdminReview | null>(null);
  const [dialogTarget, setDialogTarget] = useState<ReviewStatus>('hidden');
  const [dialogNote, setDialogNote] = useState('');
  const [dialogSubmitting, setDialogSubmitting] = useState(false);

  // Lightbox state
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const fetchReviews = useCallback(
    async (status: ReviewStatus, nextOffset: number, append: boolean) => {
      setLoading(true);
      try {
        const { data } = await api.get('/api/reviews/admin/list', {
          params: { status, limit: PAGE_LIMIT, offset: nextOffset },
        });
        const list: AdminReview[] = Array.isArray(data?.reviews) ? data.reviews : [];
        setReviews((prev) => (append ? [...prev, ...list] : list));
        setHasMore(list.length >= PAGE_LIMIT);
      } catch (err: any) {
        enqueueSnackbar(err?.response?.data?.message || '載入評論失敗', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    setOffset(0);
    setExpanded({});
    fetchReviews(statusFilter, 0, false);
  }, [statusFilter, fetchReviews]);

  const handleLoadMore = () => {
    const next = offset + PAGE_LIMIT;
    setOffset(next);
    fetchReviews(statusFilter, next, true);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // --------------------------------------------------------------------------
  // Moderate (hide / re-publish)
  // --------------------------------------------------------------------------

  const openDialog = (review: AdminReview, target: ReviewStatus) => {
    setDialogReview(review);
    setDialogTarget(target);
    setDialogNote(review.adminNote || '');
  };

  const closeDialog = () => {
    if (dialogSubmitting) return;
    setDialogReview(null);
    setDialogNote('');
  };

  const submitDialog = async () => {
    if (!dialogReview) return;
    setDialogSubmitting(true);
    try {
      const { data } = await api.patch<StatusResponse>(
        `/api/reviews/admin/${dialogReview.id}/status`,
        {
          status: dialogTarget,
          adminNote: dialogNote.trim() || undefined,
        }
      );

      // Build credit-action toast
      const baseMsg =
        dialogTarget === 'hidden' ? '已隱藏評論'
        : dialogTarget === 'cancelled' ? '已取消評論'
        : '已重新發布評論';
      let msg = baseMsg;
      if (data?.creditAction) {
        if (typeof data.creditAction.reversed === 'number' && data.creditAction.reversed > 0) {
          msg += `並扣回 NT$${data.creditAction.reversed} 購物金`;
        } else if (typeof data.creditAction.granted === 'number' && data.creditAction.granted > 0) {
          msg += `並發放 NT$${data.creditAction.granted} 購物金`;
        }
      }
      enqueueSnackbar(msg, { variant: 'success' });

      setDialogReview(null);
      setDialogNote('');
      // Refresh the current tab
      setOffset(0);
      fetchReviews(statusFilter, 0, false);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || '更新評論狀態失敗', { variant: 'error' });
    } finally {
      setDialogSubmitting(false);
    }
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        評論管理
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v: ReviewStatus) => setStatusFilter(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="已發布" value="published" />
          <Tab label="已隱藏" value="hidden" />
          <Tab label="已取消" value="cancelled" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 140 }}>建立時間</TableCell>
              <TableCell sx={{ width: 200 }}>客戶</TableCell>
              <TableCell sx={{ width: 260 }}>商品</TableCell>
              <TableCell sx={{ width: 120 }}>評分</TableCell>
              <TableCell>標題 / 內文</TableCell>
              <TableCell sx={{ width: 180 }}>圖片</TableCell>
              <TableCell sx={{ width: 100 }}>狀態</TableCell>
              <TableCell sx={{ width: 140 }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  沒有評論
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((r) => {
                const isExpanded = !!expanded[r.id];
                const fullText = r.body || '';
                const displayText = isExpanded ? fullText : truncate(fullText, 80);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatDate(r.createdAt)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.3}>
                        <Typography variant="body2">
                          {r.customer?.name || '—'}
                          {r.customer?.nickname ? ` (${r.customer.nickname})` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.customer?.email || ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {r.productTitle || `商品 #${r.productId}`}
                        </Typography>
                        <Tooltip title="在前台開啟">
                          <IconButton
                            size="small"
                            component={MuiLink}
                            href={storefrontUrl(r.brand, r.productId)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        #{r.productId} · {r.brand}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Rating value={r.rating} readOnly size="small" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      {r.title && (
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {r.title}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {displayText}
                      </Typography>
                      {fullText.length > 80 && (
                        <Button
                          size="small"
                          onClick={() => toggleExpand(r.id)}
                          startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          sx={{ mt: 0.5, minWidth: 0, px: 0.5 }}
                        >
                          {isExpanded ? '收合' : '展開'}
                        </Button>
                      )}
                      {r.adminNote && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          管理備註：{r.adminNote}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.imageUrls && r.imageUrls.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {r.imageUrls.map((url, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={url}
                              alt={`review-${r.id}-${i}`}
                              onClick={() => setLightboxUrl(url)}
                              sx={{
                                width: 44,
                                height: 44,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'pointer',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          無
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.status === 'published' ? (
                        <Chip size="small" color="success" label="已發布" />
                      ) : r.status === 'hidden' ? (
                        <Chip size="small" color="default" label="已隱藏" />
                      ) : (
                        <Chip size="small" color="error" label="已取消" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {r.status === 'published' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<VisibilityOffIcon />}
                              onClick={() => openDialog(r, 'hidden')}
                            >
                              隱藏
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => openDialog(r, 'cancelled')}
                            >
                              取消並扣回
                            </Button>
                          </>
                        )}
                        {r.status === 'hidden' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<VisibilityIcon />}
                              onClick={() => openDialog(r, 'published')}
                            >
                              重新發布
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => openDialog(r, 'cancelled')}
                            >
                              取消並扣回
                            </Button>
                          </>
                        )}
                        {r.status === 'cancelled' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<VisibilityIcon />}
                            onClick={() => openDialog(r, 'published')}
                          >
                            重新發布並補回
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {hasMore && reviews.length > 0 && (
          <Button onClick={handleLoadMore} disabled={loading} variant="outlined">
            {loading ? '載入中…' : '載入更多'}
          </Button>
        )}
      </Box>

      {/* Moderate dialog */}
      <Dialog open={!!dialogReview} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogTarget === 'hidden' ? '隱藏評論'
            : dialogTarget === 'cancelled' ? '取消評論並扣回購物金'
            : '重新發布評論'}
        </DialogTitle>
        <DialogContent>
          {dialogReview && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                客戶：{dialogReview.customer?.name} ({dialogReview.customer?.email})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                商品：{dialogReview.productTitle || `#${dialogReview.productId}`}
              </Typography>
              {dialogTarget === 'hidden' && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  此操作只會將評論從前台隱藏，<strong>購物金獎勵會保留</strong>。
                </Typography>
              )}
              {dialogTarget === 'cancelled' && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  此操作會取消此評論，並<strong>自動扣回先前發放的購物金</strong>（如客戶已使用，餘額可能變為負數）。
                </Typography>
              )}
              {dialogTarget === 'published' && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  此操作會重新發布此評論。{dialogReview.status === 'cancelled' ? '若先前已扣回購物金，將重新發放。' : ''}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            label="管理備註（選填，最多 500 字）"
            value={dialogNote}
            onChange={(e) => setDialogNote(e.target.value.slice(0, 500))}
            multiline
            rows={3}
            fullWidth
            placeholder="例：內容違反社群規範 / 已與客戶確認"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={dialogSubmitting}>
            取消
          </Button>
          <Button
            onClick={submitDialog}
            variant="contained"
            color={
              dialogTarget === 'cancelled' ? 'error'
              : dialogTarget === 'hidden' ? 'warning'
              : 'primary'
            }
            disabled={dialogSubmitting}
          >
            {dialogSubmitting
              ? '處理中…'
              : dialogTarget === 'hidden' ? '確認隱藏'
              : dialogTarget === 'cancelled' ? '確認取消並扣回'
              : '確認重新發布'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightboxUrl} onClose={() => setLightboxUrl(null)} maxWidth="md">
        <Box sx={{ position: 'relative', bgcolor: 'black' }}>
          <IconButton
            onClick={() => setLightboxUrl(null)}
            sx={{ position: 'absolute', top: 4, right: 4, color: 'white', zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {lightboxUrl && (
            <Box
              component="img"
              src={lightboxUrl}
              alt="review-full"
              sx={{
                display: 'block',
                maxWidth: '90vw',
                maxHeight: '85vh',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ReviewsAdminPage;
